import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { IVideoStream } from "../../providers/videoStreams/videoStreams.interface";
import { useUser } from "../../providers/users";
import { useVideoStream } from "../../providers/videoStreams";
import { Button, IconButton, Menu, MenuItem } from "@mui/material";
import { FiMoreVertical, FiEdit, FiEye, FiTrash2 } from "react-icons/fi";
import { CardContainer } from "./styles";
import ModalConfirmDeletion from "../ModalConfirmDeletion";

interface IProps {
  video: IVideoStream;
}

const VideoStreamCard: React.FC<IProps> = ({ video }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleAdmin } = useUser();
  const { deleteVideoStream, getPlaybackUrl } = useVideoStream();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { path } = useParams();
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    navigate(`/${path}/video-streams/${video.id}/edit`);
    handleMenuClose();
  };

  const handleViewClick = async () => {
    const playbackData = await getPlaybackUrl(video.id);
    if (playbackData) {
      window.open(playbackData.embedUrl, "_blank");
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
    handleMenuClose();
  };

  const confirmDelete = () => {
    deleteVideoStream(video.id);
    setIsDeleteModalOpen(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "ready":
        return "statusReady";
      case "processing":
        return "statusProcessing";
      case "error":
        return "statusError";
      default:
        return "";
    }
  };

  return (
    <CardContainer>
      <div className="videoThumbnail">
        {!video.thumbnailUrl ? (
          <img
            src={
              video.thumbnailUrl ||
              "https://icon2.cleanpng.com/20180612/job/aa8xsjot6.webp"
            }
            alt={video.name}
          />
        ) : (
          <div className="placeholderThumbnail">
            <span>{video.name.charAt(0)}</span>
          </div>
        )}
      </div>

      <div className="videoInfo">
        <div className="videoHeader">
          <h3 className="videoTitle">{video.name}</h3>
          <span className={`statusBadge ${getStatusClass(video.status)}`}>
            {t(video.status)}
          </span>
        </div>

        <div className="videoMeta">
          {video.duration && (
            <span className="videoDuration">
              {formatDuration(video.duration)}
            </span>
          )}
          <span className="videoDate">{formatDate(video.createdAt)}</span>
          <span className="videoViews">
            {video.viewCount} {t("views")}
          </span>
        </div>

        {video.description && (
          <p className="videoDescription">{video.description}</p>
        )}
      </div>

      <div className="videoActions">
        {/* {video.status === "ready" && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleViewClick}
            startIcon={<FiEye />}
          >
            {t("view")}
          </Button>
        )} */}

        {roleAdmin && (
          <>
            <IconButton onClick={handleMenuClick}>
              <FiMoreVertical />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEditClick}>
                <FiEdit style={{ marginRight: "8px" }} />
                {t("edit")}
              </MenuItem>

              <MenuItem onClick={handleDeleteClick}>
                <FiTrash2 style={{ marginRight: "8px" }} />
                {t("delete")}
              </MenuItem>
            </Menu>
          </>
        )}
      </div>

      {isDeleteModalOpen && (
        <ModalConfirmDeletion
          setModalConfirmDeletion={() => setIsDeleteModalOpen(false)}
          deleteFunction={deleteVideoStream}
          deleteId={video.id}
        />
      )}
    </CardContainer>
  );
};
function formatDuration(numberOfDays: number): string {
  if (numberOfDays === 1) {
    return "Single-day";
  } else if (numberOfDays > 1) {
    return `Multi-day(${numberOfDays} days)`;
  } else if (numberOfDays === 0) {
    return `No session created.`;
  } else {
    return "Invalid number of days";
  }
}

export default VideoStreamCard;
